/**
 * 生成各国商标查询网址的工具函数
 */

export interface TrademarkUrlConfig {
  nation: string;
  applicationNo: string;
}

export interface TrademarkOffice {
  nation: string;
  officeName: string;
}

export const TRADEMARK_OFFICES: TrademarkOffice[] = [
  { nation: '美国', officeName: 'USPTO (美国专利商标局)' },
  { nation: '加拿大', officeName: 'CIPO (加拿大知识产权局)' },
  { nation: '俄罗斯', officeName: 'FIPS (俄罗斯联邦知识产权局)' },
  { nation: '墨西哥', officeName: 'IMPI (墨西哥工业产权局)' },
  { nation: '韩国', officeName: 'KIPRIS (韩国知识产权信息服务)' },
  { nation: '日本', officeName: 'J-PlatPat (日本专利信息平台)' },
  { nation: '智利', officeName: 'INAPI (智利工业产权局)' },
  { nation: '巴西', officeName: 'INPI (巴西国家工业产权局)' },
  { nation: '欧盟', officeName: 'EUIPO (欧盟知识产权局)' },
  { nation: '德国', officeName: 'DPMA (德国专利商标局)' },
  { nation: '英国', officeName: 'UK IPO (英国知识产权局)' },
  { nation: '澳大利亚', officeName: 'IP Australia (澳大利亚知识产权局)' },
  { nation: '新西兰', officeName: 'IPONZ (新西兰知识产权局)' },
];

/**
 * 商标监控页仅展示已接入抓取爬虫的国家，避免录入无法同步的监控数据。
 */
export const MONITORED_NATION_OPTIONS = TRADEMARK_OFFICES.map((office) => ({
  value: office.nation,
  label: office.nation,
}));

/**
 * 匹配当前国家对应的商标局配置
 */
function findTrademarkOffice(nation: string) {
  return TRADEMARK_OFFICES.find(office => nation.includes(office.nation));
}

/**
 * 根据国家和申请号生成对应的商标查询URL
 */
export function getTrademarkUrl({ nation, applicationNo }: TrademarkUrlConfig): string | null {
  if (!nation || !applicationNo) {
    return null;
  }

  // 美国
  if (nation.includes('美国')) {
    // 美国申请号格式处理：如果是8位数字，需要添加前缀
    let formattedNo = applicationNo;
    if (/^\d{8}$/.test(applicationNo)) {
      formattedNo = `${applicationNo}`;
    }
    return `https://tsdr.uspto.gov/#caseSearchType=US_APPLICATION&caseType=DEFAULT&searchType=statusSearch&caseNumber=${formattedNo}`;
  }

  // 加拿大
  if (nation.includes('加拿大')) {
    return `https://ised-isde.canada.ca/cipo/trademark-search/${applicationNo}`;
  }

  // 俄罗斯
  if (nation.includes('俄罗斯')) {
    return `https://www1.fips.ru/fips_servl/fips_servlet?DB=RUTMAP&rn=1895&DocNumber=${applicationNo}`;
  }

  if (nation.includes('墨西哥')) {
    return `https://acervomarcas.impi.gob.mx:8181/marcanet/vistas/common/datos/bsqExpedienteCompleto.pgi`;
  }

  if (nation.includes('韩国')) {
    return `https://www.kipris.or.kr/khome/detail/newWindow.do?applno=${applicationNo}&right=ktm`;
  }

  if (nation.includes('日本')) {
    return `https://www.j-platpat.inpit.go.jp/`;
  }

  if (nation.includes('智利')) {
    return `https://buscadormarcas.inapi.cl/Marca/BuscarMarca.aspx`;
  }

  if (nation.includes('巴西')) {
    return `https://busca.inpi.gov.br/pePI/servlet/LoginController?action=login`;
  }

  if (nation.includes('欧盟')) {
    return `https://euipo.europa.eu/eSearch/#details/trademarks/${applicationNo}`;
  }

  if (nation.includes('德国')) {
    return `https://register.dpma.de/DPMAregister/marke/register/${applicationNo}/DE`;
  }

  if (nation.includes('英国')) {
    return `https://trademarks.ipo.gov.uk/ipo-tmcase/page/Results/1/${applicationNo}`;
  }

  if (nation.includes('澳大利亚')) {
    return `https://search.ipaustralia.gov.au/trademarks/search/view/${applicationNo}/`;
  }

  if (nation.includes('新西兰')) {
    return `https://app.iponz.govt.nz/app/Extra/IP/TM/Qbe.aspx?sid=639123042622478016&op=EXTRA_tm_qbe&fcoOp=EXTRA__Default&directAccess=true`;
  }

  // 其他国家暂不支持
  return null;
}

/**
 * 检查是否支持跳转到商标查询网址
 */
export function canOpenTrademarkUrl(nation: string, applicationNo = '-'): boolean {
  return Boolean(getTrademarkUrl({ nation, applicationNo }));
}

/**
 * 获取国家对应的商标局名称
 */
export function getTrademarkOfficeName(nation: string): string {
  return findTrademarkOffice(nation)?.officeName || '';
}
